import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { FileBadge, FileText } from "lucide-react";
import DocLightbox from "@/components/DocLightbox";
import { postUpload } from "@/components/ConflittoNome";

export function ciBadge(scad) {
  if (!scad) return null;
  const d = Math.ceil((new Date(scad) - new Date()) / 86400000);
  if (d < 0) return <span data-testid="ci-badge-scaduto" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-red-600 bg-red-500/10 border-red-500/30">Scaduto da {Math.abs(d)}gg</span>;
  if (d <= 60) return <span data-testid="ci-badge-scadenza" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-[#FFB020] bg-[#FFB020]/10 border-[#FFB020]/30">In scadenza ({d}gg)</span>;
  return <span data-testid="ci-badge-valido" className="px-2 py-0.5 rounded-full text-[10px] font-bold border text-emerald-600 bg-emerald-400/10 border-emerald-400/30">Valido</span>;
}

function DocThumb({ file, onOpen }) {
  const [url, setUrl] = useState(null);
  const isImg = /\.(jpg|jpeg|png|webp|heic|heif|gif)$/i.test(file.original_filename || "") || (file.content_type || "").startsWith("image/");
  useEffect(() => {
    let active = true; let obj;
    if (isImg) api.get(`/files/${file.id}/download`, { responseType: "blob" }).then((r) => { if (!active) return; obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => {});
    return () => { active = false; if (obj) URL.revokeObjectURL(obj); };
  }, [file.id]); // eslint-disable-line
  return (
    <button data-testid={`doc-thumb-${file.id}`} onClick={() => onOpen(file)} title={file.original_filename}
            className="w-16 h-16 rounded-lg border border-[var(--border)] overflow-hidden bg-[#F7F7F8] flex items-center justify-center shrink-0 hover:border-[#FC82ED]/60 transition-colors">
      {isImg && url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <FileText size={22} className="text-[#FC82ED]" />}
    </button>
  );
}

function DocSlot({ entityType, entityId, tipo, label, files, onDone, onView, withScadenza }) {
  const [busy, setBusy] = useState(false);
  const current = files.filter((f) => f.doc_tipo === tipo);
  const up = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("entity_type", entityType); fd.append("entity_id", entityId);
      fd.append("kind", "documento"); fd.append("doc_tipo", tipo);
      await postUpload(fd);
      toast.success(`${label} caricato`); onDone();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const setScad = async (f, val) => {
    try { await api.patch(`/files/${f.id}`, { scadenza: val }); toast.success("Scadenza aggiornata"); onDone(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm font-medium flex items-center gap-2"><FileBadge size={14} className="text-[#FC82ED]"/> {label}</span>
        <label data-testid={`upload-${tipo}`} className="px-3 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:border-[#FC82ED]/40 cursor-pointer">
          {busy ? "Caricamento…" : (current.length ? "Sostituisci" : "Carica")}
          <input type="file" className="hidden" onChange={up} />
        </label>
      </div>
      {current.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {current.map((f) => (
            <div key={f.id} className="flex flex-col items-center gap-1 w-16">
              <DocThumb file={f} onOpen={onView} />
              <button data-testid={`doc-${f.id}`} onClick={() => onView(f)} className="text-[10px] text-slate-500 hover:text-[#FC82ED] truncate w-full text-center" title={f.original_filename}>{f.original_filename}</button>
            </div>
          ))}
        </div>
      )}
      {withScadenza && current.length > 0 && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <label className="text-[11px] text-[var(--muted)]">Scadenza documento</label>
          <input data-testid={`scad-${tipo}`} type="date" value={current[0].scadenza || ""} onChange={(e) => setScad(current[0], e.target.value)}
                 className="px-2 py-1.5 rounded-lg bg-white border border-[var(--border)] text-xs outline-none focus:border-[#FC82ED]" />
          {ciBadge(current[0].scadenza)}
        </div>
      )}
    </div>
  );
}

export function EntityDocs({ entityType, entityId }) {
  const [files, setFiles] = useState([]);
  const [view, setView] = useState(null);
  const load = () => api.get(`/files?entity_type=${entityType}&entity_id=${entityId}&kind=documento`).then((r) => setFiles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line
  return (
    <div className="rounded-xl border border-[var(--border)] p-3 space-y-3" data-testid="docs-section">
      <DocSlot entityType={entityType} entityId={entityId} tipo="documento_identita" label="Documento d'identità" files={files} onDone={load} onView={setView} withScadenza />
      <DocSlot entityType={entityType} entityId={entityId} tipo="codice_fiscale" label="Codice Fiscale" files={files} onDone={load} onView={setView} />
      {view && <DocLightbox file={view} onClose={() => setView(null)} />}
    </div>
  );
}
